// src/ecrans/operations/EcranOperations.jsx — OPÉRATIONS NEST (maquette
// v677, « Admin › Opérations », relevés 238 à 249).
//
// `EnteteOperations` : l'en-tête à six tuiles et la barre des six
// sous-onglets, posés au-dessus de chaque sous-onglet d'Opérations.
// `VueMenages` : « Les ménages » (frise du jour, puis « Demain »).
//
// Lu SEULEMENT par les portes : ecran_journee_lire { jours: 2 },
// services_orchestrateur_hourly_list (demandes d'horaire),
// ecran_controles_lire, ecran_reprises_lire, ecran_incidents_lire,
// ecran_stock_a_apporter_lire, ecran_consignes_lire.
// HORS LOT : « qui paie » des incidents (argent). ÉCRITURES hors lot : déplacer
// des horaires, intervention, confier à un agent, désigner un contrôle.

import { useMemo, useState } from "react";
import { useLecture, nombreDe } from "../portes";
import {
  StyleNest,
  EnTete,
  Tuile,
  Bascule,
  EtatLecture,
  LienAncien,
  ICO,
  C,
  aujourdhuiParis,
  jourEnLettres,
  jourCourt,
  mot,
  pl,
} from "../nest";
import { tuilesJour, friseDuJour, demain, lignesStock } from "./operations-calculs";
import { chiffreSuivi, couvertureDe, idsNonSuivis, jourSuivi, nonCouverts, phraseNonCouverts } from "../couverture";

const SANS_OK = { accepteSansOk: true };

/** tab id de l'appli ↔ sous-onglet de la maquette. */
export const OPERATIONS_TABS = {
  menages: "Les ménages",
  "menage-controle": "Contrôle",
  incidents: "Incidents",
  "orchestrateur-horaires": "Demandes d'horaires",
  stock: "Stock",
  "mes-taches": "Tâches",
};
const ICONES_ONGLETS = {
  menages: ICO.horloge,
  "menage-controle": ICO.controle,
  incidents: ICO.attend,
  "orchestrateur-horaires": ICO.echange,
  stock: ICO.boite,
  "mes-taches": ICO.regles,
};

const CSS = `
.nops-hdr{display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:10px 12px;margin-bottom:12px}
.nops-nav{display:flex;align-items:center;gap:2px;background:#F8F9F8;border:1px solid ${C.bord};border-radius:11px;padding:3px}
.nops-nav button{all:unset;cursor:pointer;min-width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:12.5px;font-weight:600;padding:0 8px;flex-direction:column;line-height:1.1}
.nops-nav button[disabled]{opacity:.35;cursor:default}
.nops-nav small{font-size:9.5px;font-weight:400;color:${C.grisD}}
.nops-grille{display:grid;grid-template-columns:minmax(0,1fr) 270px;gap:14px;align-items:start}
.nops-frise{background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:12px}
.nops-axe{display:flex;margin-left:176px;font-size:9.5px;font-weight:600;color:${C.grisD};position:relative;height:18px}
.nops-grp{display:flex;align-items:center;gap:8px;font-size:10.5px;font-weight:700;letter-spacing:.06em;color:${C.grisD};margin:12px 0 4px}
.nops-grp.al{background:${C.aubP};color:${C.aub};border-radius:8px;padding:6px 10px}
.nops-r{display:flex;border-bottom:1px solid #F0F2F1;min-height:46px}
.nops-n{width:176px;flex-shrink:0;padding:4px 6px 4px 0;font-size:11.5px;font-weight:600}
.nops-n small{display:block;font-weight:400;font-size:10.5px;color:${C.aub}}
.nops-z{position:relative;flex:1;min-width:0}
.nops-bk{position:absolute;top:12px;height:24px;border-radius:7px;padding:0 7px;display:flex;align-items:center;font-size:10px;font-weight:600;white-space:nowrap;overflow:hidden;box-sizing:border-box}
.nops-mk{position:absolute;top:0;font-size:9px;font-weight:600;border-radius:5px;padding:0 4px;white-space:nowrap}
.nops-cote{background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:12px}
.nops-dem{background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:12px;margin-bottom:8px;display:flex;flex-wrap:wrap;gap:8px 14px;align-items:center;font-size:12px}
.nops-dem b{font-size:13px}
@media (max-width:900px){.nops-grille{grid-template-columns:1fr}}
@media (max-width:640px){.nops-n{width:110px}.nops-axe{margin-left:110px}}
`;

const H_DEBUT = 8 * 60;
const H_FIN = 19 * 60;
const pc = (min) => `${Math.max(0, Math.min(100, ((min - H_DEBUT) / (H_FIN - H_DEBUT)) * 100))}%`;

/** L'en-tête d'Opérations (six tuiles) et sa barre de sous-onglets. */
export function EnteteOperations({ tab, onTab }) {
  const today = useMemo(() => aujourdhuiParis(), []);
  const [journee, relire] = useLecture("ecran_journee_lire", { jours: 2 });
  const [horaires] = useLecture("services_orchestrateur_hourly_list", {}, "", SANS_OK);
  // LA RÈGLE DES CHIFFRES (src/ecrans/couverture.js) : on compte sur les
  // logements SUIVIS seulement (jourSuivi) ; tous suivis → le vrai chiffre ;
  // une partie → « sur N logements suivis » ; aucun → « — ».
  const cv = couvertureDe(journee);
  const t = journee.etat === "ok" ? tuilesJour(jourSuivi(journee.data.jours[0], cv)) : null;
  const c = (k) => chiffreSuivi(t ? t[k] : NaN, cv);
  // « faits » se lit sur le total : total inconnu → « — », pas « 0/0 ».
  const total = c("total");
  const totalSur = typeof total.valeur === "number" ? t.total : undefined;
  const n = (k) => {
    if (k === "faits") return totalSur === undefined ? total.valeur : t.faits;
    return c(k).valeur;
  };
  const nbHoraires = nombreDe(horaires, (d) => (Array.isArray(d.pending) ? d.pending.length : NaN));
  const [controles] = useLecture("ecran_controles_lire", {});
  const [reprises] = useLecture("ecran_reprises_lire", {});
  const [incidents] = useLecture("ecran_incidents_lire", {});
  const nbFaits = nombreDe(controles, (d) => d.compteurs.faits);
  const nbAFaire = controles.etat === "ok" ? controles.data.compteurs.a_faire : 0;
  const nbReprises = reprises.etat === "ok" ? reprises.data.reprises.length : 0;
  const nbIncidents = nombreDe(incidents, (d) => d.compteurs.total);
  return (
    <div className="nest" data-testid="entete-operations">
      <StyleNest />
      <EnTete sous={`${jourEnLettres(today)} · ménages, contrôles, incidents, stock`} titre="Opérations">
        <Tuile n={n("faits")} sur={totalSur} detail={total.sur} libelle={mot(totalSur === undefined ? null : t.total, "ménage fait", "ménages faits")} testId="ops-tuile-menages" onClick={() => onTab("menages")} />
        <Tuile n={n("departs")} detail={c("departs").sur} libelle={`${mot(t ? n("departs") : null, "départ")}${t && t.departsHeures ? ` · ${t.departsHeures}` : ""}`} testId="ops-tuile-departs" onClick={() => onTab("menages")} />
        <Tuile n={n("arrivees")} detail={c("arrivees").sur} libelle={`${mot(t ? n("arrivees") : null, "arrivée")}${t && t.arriveesHeures ? ` · ${t.arriveesHeures}` : ""}`} testId="ops-tuile-arrivees" onClick={() => onTab("menages")} />
        <Tuile n={nbFaits} sur={controles.etat === "ok" ? nbFaits + nbAFaire : undefined} alerte={nbAFaire > 0 || nbReprises > 0}
          libelle={`${mot(nbFaits, "contrôle fait", "contrôles faits")}${nbReprises ? ` · ${pl(nbReprises, "reprise en attente", "reprises en attente")}` : ""}`}
          testId="ops-tuile-controles" onClick={() => onTab("menage-controle")} />
        <Tuile n={nbIncidents} alerte={nbIncidents > 0} libelle={mot(nbIncidents, "incident à traiter", "incidents à traiter")} testId="ops-tuile-incidents" onClick={() => onTab("incidents")} />
        <Tuile n={nbHoraires} alerte={nbHoraires > 0} libelle={mot(nbHoraires, "demande d'horaire", "demandes d'horaire")} testId="ops-tuile-horaires" onClick={() => onTab("orchestrateur-horaires")} />
      </EnTete>
      <EtatLecture lecture={journee.etat === "echec" ? journee : null} quoi="La journée" onRelire={relire} />
      <Bascule
        testId="ops-bascule"
        actif={tab}
        onChoisir={onTab}
        entrees={Object.entries(OPERATIONS_TABS).map(([id, libelle]) => ({
          id,
          libelle,
          ic: ICONES_ONGLETS[id],
          nb: id === "orchestrateur-horaires" && Number.isFinite(nbHoraires) && nbHoraires > 0 ? nbHoraires : null,
        }))}
      />
      <style>{CSS}</style>
    </div>
  );
}

function Ligne({ l, maintenant, nonSuivi }) {
  const m = l.menage;
  const tonBk = m.fini
    ? { background: C.vertP, border: `1px solid ${C.vertTrait}`, color: C.vertF }
    : m.agent
      ? { background: "#fff", border: "1px solid #6B7672", color: "#1C3A33" }
      : { background: "#F4F6F5", border: "1px dashed #98A2AD", color: C.gris };
  const largeur = l.fin !== null ? `calc(${pc(l.fin)} - ${pc(l.debut)})` : "64px";
  return (
    <div className="nops-r" data-testid={`ops-ligne-${m.id}`}>
      <div className="nops-n">
        {l.nom}
        {l.raison ? <small>{l.raison}</small> : null}
        {nonSuivi ? <small style={{ color: C.grisD }} data-testid="ops-non-suivi">non suivi</small> : null}
      </div>
      <div className="nops-z">
        {maintenant !== null ? <span aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: pc(maintenant), width: 2, background: C.aub }} /> : null}
        {l.depart ? (
          <span className="nops-mk" style={{ left: pc(l.depart.h), background: C.aubP, color: C.aub }} title={l.depart.heureDite ? "heure annoncée" : "heure du contrat"}>
            ↓ {Math.floor(l.depart.h / 60)}h {l.depart.qui ? l.depart.qui.split(" ")[0] : ""}
          </span>
        ) : null}
        {l.arrivee ? (
          <span className="nops-mk" style={{ left: pc(l.arrivee.h), background: C.vertP, color: C.vertF }} title={l.arrivee.heureDite ? "heure annoncée" : "heure du contrat"}>
            ↑ {Math.floor(l.arrivee.h / 60)}h {l.arrivee.qui ? l.arrivee.qui.split(" ")[0] : ""}
          </span>
        ) : null}
        {l.debut !== null ? (
          <span className="nops-bk" style={{ left: pc(l.debut), width: largeur, ...tonBk }} title={l.menage.heure ? "heure fixée" : "heure non fixée : après le départ"}>
            {m.agent ? m.agent.prenom : "personne"}
            {m.fini ? " · fait" : ""}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** « Les ménages » : frise du jour choisi (aujourd'hui ou demain), puis « Demain ». */
export function VueMenages({ onAncien }) {
  const [journee, relire] = useLecture("ecran_journee_lire", { jours: 2 });
  const [i, setI] = useState(0);
  const [mode, setMode] = useState("logement");
  const [cherche, setCherche] = useState("");
  // « Validés » replié au départ (maquette : « Tout voir ▾ »).
  const [validesOuverts, setValidesOuverts] = useState(false);
  const [stock, relireStock] = useLecture("ecran_stock_a_apporter_lire", {});
  const [incidents] = useLecture("ecran_incidents_lire", {});
  const [controles] = useLecture("ecran_controles_lire", {});
  const [consignes] = useLecture("ecran_consignes_lire", {});
  if (journee.etat !== "ok")
    return (
      <div className="nest">
        <StyleNest />
        <style>{CSS}</style>
        <div className="nest-page">
          <EtatLecture lecture={journee} quoi="Les ménages" onRelire={relire} />
        </div>
      </div>
    );
  const jours = journee.data.jours || [];
  const jour = jours[i] || null;
  const trous = phraseNonCouverts(nonCouverts(journee.data));
  const dm = demain(jours[1]);
  const f = friseDuJour(jour);
  const q = cherche.trim().toLowerCase();
  const garde = (l) => !q || l.nom.toLowerCase().includes(q);
  const tri = (liste) =>
    mode === "agent"
      ? [...liste].sort((a, b) => String(a.menage.agent ? a.menage.agent.prenom : "~").localeCompare(String(b.menage.agent ? b.menage.agent.prenom : "~")))
      : liste;
  const enDanger = f.aRegler.filter((l) => !l.menage.agent);
  // La règle des chiffres : « La journée se déroule bien » seulement si TOUS
  // les logements sont suivis ; aucun suivi → « — », on ne rassure pas.
  const cv = couvertureDe(journee);
  // Les NOMBRES de ménages ne comptent que les logements suivis ; les lignes
  // (frise, « Demain ») montrent tout le travail réel, les logements non
  // suivis marqués « non suivi ». « N sans agent » est du vrai travail : il se
  // compte sur TOUT le jour (un ménage sans agent reste à confier, suivi ou non).
  const nonSuivis = idsNonSuivis(cv);
  const estNonSuivi = (logement) => Boolean(logement && nonSuivis.has(logement.id));
  const menagesDuJour = chiffreSuivi(jour ? jourSuivi(jour, cv).menages.length : 0, cv);
  const nbMenages = menagesDuJour.valeur;
  const dmSuivi = demain(jourSuivi(jours[1], cv));
  const menagesDemain = dmSuivi ? chiffreSuivi(dmSuivi.nb, cv) : null;
  const hm = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" }).format(new Date());
  const maintenantMin = Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5));
  const groupe = (titre, liste, alerte) =>
    liste.filter(garde).length ? (
      <>
        <div className={`nops-grp${alerte ? " al" : ""}`}>
          {titre} <span>{liste.filter(garde).length}</span>
        </div>
        {tri(liste.filter(garde)).map((l) => <Ligne key={l.menage.id} l={l} nonSuivi={estNonSuivi(l.menage.logement)} maintenant={i === 0 && maintenantMin >= H_DEBUT && maintenantMin <= H_FIN ? maintenantMin : null} />)}
      </>
    ) : null;
  return (
    <div className="nest" data-testid="ops-menages">
      <StyleNest />
      <style>{CSS}</style>
      <div className="nest-page">
        <div className="nops-hdr">
          <span className="nops-nav">
            <button type="button" aria-label="Jour précédent" disabled={i === 0} onClick={() => setI(0)}>‹</button>
            <button type="button" style={{ cursor: "default" }}>
              {jour ? jourCourt(jour.date) : "—"}
              <small>{i === 0 ? "aujourd'hui" : "demain"}</small>
            </button>
            <button type="button" aria-label="Jour suivant" disabled={i === 1 || jours.length < 2} onClick={() => setI(1)}>›</button>
          </span>
          <span data-testid="ops-menages-etat" style={{ fontSize: 12.5, fontWeight: 600, color: enDanger.length ? C.aub : menagesDuJour.tousSuivis ? C.vertF : C.gris }}>
            {enDanger.length
              ? `${pl(enDanger.length, "ménage")} en danger`
              : menagesDuJour.tousSuivis
                ? "La journée se déroule bien"
                : nbMenages === "—"
                  ? "Ménages non comptés"
                  : "Ménages comptés en partie"}
            <small style={{ display: "block", fontWeight: 400, fontSize: 11 }}>
              {enDanger.length
                ? enDanger.map((l) => l.nom).join(", ")
                : nbMenages === "—"
                  ? "— ménage : un logement sans source peut en cacher un"
                  : [pl(nbMenages, "ménage"), menagesDuJour.sur].filter(Boolean).join(" ")}
            </small>
          </span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="nest-bt" disabled title="Une écriture : hors de ce lot de lecture.">Déplacer des horaires</button>
            <button type="button" className="nest-bt" disabled title="Une écriture : hors de ce lot de lecture.">+ Intervention</button>
            <LienAncien pour="confier, lancer un contrôle" onClick={onAncien} testId="ops-ancien" />
          </span>
        </div>
        {trous ? <p className="nest-manque" data-testid="ops-non-couverts">{trous}</p> : null}
        <div className="nops-grille">
          <div className="nops-frise">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <span className="nest-seg">
                <button type="button" className={mode === "logement" ? "on" : ""} onClick={() => setMode("logement")}>Par logement</button>
                <button type="button" className={mode === "agent" ? "on" : ""} onClick={() => setMode("agent")}>Par agent</button>
              </span>
              <input className="nest-champ" placeholder="un logement…" value={cherche} onChange={(e) => setCherche(e.target.value)} />
            </div>
            <div className="nops-axe" aria-hidden="true">
              {[8, 10, 12, 14, 16, 18].map((h) => (
                <span key={h} style={{ position: "absolute", left: pc(h * 60) }}>{h}h</span>
              ))}
              {i === 0 && maintenantMin >= H_DEBUT && maintenantMin <= H_FIN ? (
                <span style={{ position: "absolute", left: pc(maintenantMin), transform: "translateX(-50%)", background: C.aub, color: "#fff", borderRadius: 9, padding: "0 6px" }}>{hm.replace(":", "h")}</span>
              ) : null}
            </div>
            {jour && !jour.menages.length ? (
              <p className="nest-etat">
                {!menagesDuJour.tousSuivis
                  ? `Aucun ménage lu ${i === 0 ? "aujourd'hui" : "demain"} : un logement sans source peut en cacher un.`
                  : `Aucun ménage prévu ${i === 0 ? "aujourd'hui" : "demain"}.`}
              </p>
            ) : null}
            {groupe("À RÉGLER", f.aRegler, true)}
            {groupe("À VENIR", f.aVenir)}
            {f.valides.filter(garde).length ? (
              <div className="nops-grp" data-testid="ops-valides">
                VALIDÉS <span>{f.valides.filter(garde).length}</span>
                <button type="button" onClick={() => setValidesOuverts((v) => !v)} style={{ all: "unset", cursor: "pointer", marginLeft: "auto", fontWeight: 600, letterSpacing: 0, color: C.encre, fontSize: 11 }} aria-expanded={validesOuverts}>
                  {validesOuverts ? "Replier ▴" : "Tout voir ▾"}
                </button>
              </div>
            ) : null}
            {validesOuverts ? tri(f.valides.filter(garde)).map((l) => <Ligne key={l.menage.id} l={l} nonSuivi={estNonSuivi(l.menage.logement)} maintenant={null} />) : null}
          </div>
          <div className="nops-cote">
            <p className="nest-inter" style={{ marginTop: 0 }}>Il en faut maintenant</p>
            <EtatLecture lecture={stock} quoi="Le stock à apporter" onRelire={relireStock} />
            {stock.etat === "ok" && !lignesStock(stock.data).length ? <p className="nest-etat">Rien à apporter.</p> : null}
            {stock.etat === "ok"
              ? lignesStock(stock.data).map((l) => (
                  <p key={l.cle} style={{ fontSize: 12, margin: "0 0 6px" }} data-testid="ops-stock-ligne">
                    <b style={{ fontWeight: 600 }}>{l.nom}</b> — {l.texte}
                  </p>
                ))
              : null}
            <p className="nest-inter">À décider</p>
            <EtatLecture lecture={incidents.etat === "echec" ? incidents : null} quoi="Incidents" />
            <EtatLecture lecture={controles.etat === "echec" ? controles : null} quoi="Contrôles" />
            <p style={{ fontSize: 12, margin: "0 0 4px" }} data-testid="ops-decider-incidents">{pl(nombreDe(incidents, (d) => d.compteurs.total), "incident")}</p>
            <p style={{ fontSize: 12, margin: 0 }} data-testid="ops-decider-controles">{pl(nombreDe(controles, (d) => d.compteurs.a_faire), "contrôle à traiter", "contrôles à traiter")}</p>
          </div>
        </div>
        {i === 0 && dm ? (
          <>
            <p className="nest-inter" style={{ color: C.vertF }} data-testid="ops-demain">
              Demain, {jourEnLettres(dm.date).toLowerCase()} · {pl(menagesDemain.valeur, "ménage")}{menagesDemain.sur ? ` ${menagesDemain.sur}` : ""} · {/* un ménage sans agent connu est du vrai travail ; seul un 0 peut cacher un ménage non lu */}{menagesDemain.valeur === "—" && !dm.sansAgent ? "—" : dm.sansAgent} sans agent
            </p>
            {dm.lignes.map((l, k) => (
              <div key={l.id} className="nops-dem">
                <b>{l.nom}</b>
                {estNonSuivi(((jours[1] && jours[1].menages) || [])[k] && jours[1].menages[k].logement) ? <span style={{ color: C.grisD }} data-testid="ops-demain-non-suivi">non suivi</span> : null}
                <span>{l.depart ? `Départ ${l.depart.qui || ""}${l.depart.heure ? ` · ${l.depart.heure}` : ""}` : "Pas de départ lu"}</span>
                <span style={{ color: l.agent ? C.vertF : C.aub, fontWeight: 600 }}>Ménage · {l.agent || "sans agent"}</span>
                <span>Contrôle · {l.controleur || "à désigner"}</span>
              </div>
            ))}
            <button type="button" className="nest-bt" onClick={() => setI(1)}>Voir la frise de demain ›</button>
          </>
        ) : null}
        <div className="nest-ligne" style={{ marginTop: 12, cursor: "default" }}>
          <span className="co">
            <span className="t" data-testid="ops-consignes">Consignes internes · {nombreDe(consignes, (d) => d.nombre)}</span>
            <span className="s">Un mot à laisser à un agent, sur un logement — visible de lui seul.</span>
          </span>
        </div>
        <EtatLecture lecture={consignes.etat === "echec" ? consignes : null} quoi="Les consignes" />
      </div>
    </div>
  );
}
